import { Add, Delete, Edit, Warning } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogContent,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "link"],
        [{ color: [] }, { background: [] }],
        ["clean"],
    ],
};

const quillFormats = [
    "header", "bold", "italic", "underline", "strike",
    "list", "bullet", "indent", "align",
    "blockquote", "link", "color", "background",
];

const TYPE_COLORS = {
    info: "info",
    message: "primary",
    warning: "warning",
    alert: "error",
};

const EMPTY_FORM = {
    notice_id: "",
    notice_title: "",
    notice_content: "",
    notice_type: "info",
    notice_status: "active",
};

export default function NoticeAdmin({ snackbar }) {
    const apiUrl = process.env.REACT_APP_API;

    /* ─── State ─── */
    const [notices, setNotices] = useState([]);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [action, setAction] = useState(""); // "add" | "update" | "delete"
    const [open, setOpen] = useState(""); // "delete" | "form"
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    /* ─── Fetch ─── */
    useEffect(() => {
        fetchNotices(1);
    }, []);

    const fetchNotices = (p = page) => {
        axios
            .get(`${apiUrl}/admin/notice/getall?page=${p}`)
            .then((res) => {
                if (res.data.status) {
                    if (p === 1) {
                        setNotices(res.data.data);
                    } else {
                        setNotices((prev) => [...prev, ...res.data.data]);
                    }
                    setPage(p);
                    // Check if there might be more (if returned count equals typical page size)
                    setHasMore(res.data.data.length >= 10);
                } else {
                    if (p === 1) setNotices([]);
                }
            })
            .catch(() => setNotices([]));
    };

    /* ─── Helpers ─── */
    const resetForm = () => setForm({ ...EMPTY_FORM });

    const openAdd = () => {
        resetForm();
        setOpen("form");
    };

    const openEdit = (row) => {
        setForm({
            notice_id: row.notice_id,
            notice_title: row.notice_title,
            notice_content: row.notice_content,
            notice_type: row.notice_type,
            notice_status: row.notice_status,
        });
        setOpen("form");
    };

    const openDelete = (notice_id) => {
        setForm((prev) => ({ ...prev, notice_id }));
        setOpen("delete");
    };

    /* ─── CRUD ─── */
    const handleSubmit = () => {
        const isUpdate = Boolean(form.notice_id);
        setAction(isUpdate ? "update" : "add");

        const fd = new FormData();
        fd.append("notice_title", form.notice_title);
        fd.append("notice_content", form.notice_content);
        fd.append("notice_type", form.notice_type);
        fd.append("notice_status", form.notice_status);
        if (isUpdate) fd.append("notice_id", form.notice_id);

        const url = isUpdate
            ? `${apiUrl}/admin/notice/update`
            : `${apiUrl}/admin/notice/add`;

        axios.post(url, fd).then((res) => {
            setAction("");
            snackbar(res.data.message || res.data.msg || (res.data.status ? "Success" : "Error"));
            if (res.data.status) {
                resetForm();
                setOpen("");
                fetchNotices(1);
            }
        });
    };

    const handleDelete = () => {
        setAction("delete");
        const fd = new FormData();
        fd.append("notice_id", form.notice_id);
        axios.post(`${apiUrl}/admin/notice/delete`, fd).then((res) => {
            setAction("");
            snackbar(res.data.message || res.data.msg || (res.data.status ? "Deleted" : "Error"));
            if (res.data.status) {
                setOpen("");
                fetchNotices(1);
            }
        });
    };

    /* ─── UI ─── */
    return (
        <Box className="content" pt={1}>
            <Container>

                {/* ── Add Button ── */}
                <Stack direction="row" justifyContent="flex-end" mb={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={openAdd}
                    >
                        Add Notice
                    </Button>
                </Stack>

                {/* ── Notices List ── */}
                {notices.length === 0 ? (
                    <Alert severity="info">No notices found</Alert>
                ) : (
                    <Grid container spacing={2}>
                        {notices.map((row) => (
                            <Grid item xs={12} md={6} key={row.notice_id}>
                                <Card variant="outlined">
                                    <CardContent>
                                        {/* Header row */}
                                        <Stack
                                            direction="row"
                                            alignItems="flex-start"
                                            justifyContent="space-between"
                                            spacing={1}
                                            mb={1}
                                        >
                                            <Typography fontWeight={700} fontSize={15} sx={{ flex: 1 }}>
                                                {row.notice_title}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5}>
                                                <Chip
                                                    label={row.notice_type}
                                                    size="small"
                                                    color={TYPE_COLORS[row.notice_type] || "default"}
                                                />
                                                <Chip
                                                    label={row.notice_status}
                                                    size="small"
                                                    variant="outlined"
                                                    color={row.notice_status === "active" ? "success" : "default"}
                                                />
                                            </Stack>
                                        </Stack>

                                        {/* Content preview (strip HTML) */}
                                        <Typography
                                            fontSize={13}
                                            color="text.secondary"
                                            mb={1.5}
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                            dangerouslySetInnerHTML={{ __html: row.notice_content }}
                                        />

                                        <Typography fontSize={11} color="grey.500" mb={1.5}>
                                            {row.notice_created_at}
                                        </Typography>

                                        <Divider sx={{ fontSize: 11, mb: 1 }}>ACTIONS</Divider>

                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                fullWidth
                                                startIcon={<Delete />}
                                                onClick={() => openDelete(row.notice_id)}
                                            >
                                                Delete
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="info"
                                                size="small"
                                                fullWidth
                                                startIcon={<Edit />}
                                                onClick={() => openEdit(row)}
                                            >
                                                Edit
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Load more */}
                {hasMore && (
                    <Box textAlign="center" mt={3}>
                        <Button variant="outlined" onClick={() => fetchNotices(page + 1)}>
                            Load More
                        </Button>
                    </Box>
                )}

                {/* ─── Add / Edit Dialog ─── */}
                <Dialog
                    open={open === "form"}
                    fullWidth
                    maxWidth="md"
                    onClose={() => setOpen("")}
                >
                    <DialogContent>
                        <Typography fontWeight={700} fontSize={18} mb={2}>
                            {form.notice_id ? "Edit Notice" : "Add Notice"}
                        </Typography>

                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Notice Title"
                                size="small"
                                value={form.notice_title}
                                onChange={(e) => setForm((p) => ({ ...p, notice_title: e.target.value }))}
                            />

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Notice Type</InputLabel>
                                    <Select
                                        label="Notice Type"
                                        value={form.notice_type}
                                        onChange={(e) => setForm((p) => ({ ...p, notice_type: e.target.value }))}
                                    >
                                        <MenuItem value="info">Info</MenuItem>
                                        <MenuItem value="message">Message</MenuItem>
                                        <MenuItem value="warning">Warning</MenuItem>
                                        <MenuItem value="alert">Alert</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        label="Status"
                                        value={form.notice_status}
                                        onChange={(e) => setForm((p) => ({ ...p, notice_status: e.target.value }))}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>

                            {/* react-quill editor */}
                            <Box>
                                <Typography fontSize={12} color="text.secondary" mb={0.5}>
                                    Notice Content
                                </Typography>
                                <ReactQuill
                                    theme="snow"
                                    value={form.notice_content}
                                    onChange={(val) => setForm((p) => ({ ...p, notice_content: val }))}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    style={{ minHeight: 200 }}
                                />
                            </Box>

                            <Stack direction="row" spacing={1} pt={1}>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    fullWidth
                                    onClick={() => setOpen("")}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color={form.notice_id ? "info" : "primary"}
                                    fullWidth
                                    disabled={action === "add" || action === "update"}
                                    onClick={handleSubmit}
                                >
                                    {action === "add" || action === "update" ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : form.notice_id ? (
                                        "Update Notice"
                                    ) : (
                                        "Add Notice"
                                    )}
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                </Dialog>

                {/* ─── Delete Confirm Dialog ─── */}
                <Dialog
                    open={open === "delete"}
                    fullWidth
                    maxWidth="xs"
                    onClose={() => setOpen("")}
                >
                    <DialogContent>
                        <Box textAlign="center">
                            <Warning sx={{ width: 80, height: 80 }} color="error" />
                            <Typography color="error" fontSize={18} fontWeight={600}>
                                Delete this notice?
                            </Typography>
                            <Typography color="grey" fontSize={12}>
                                *This action cannot be undone
                            </Typography>
                        </Box>
                        <Stack mt={2} spacing={1}>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={action === "delete"}
                                onClick={handleDelete}
                            >
                                {action === "delete" ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
                            </Button>
                            <Button variant="outlined" color="info" onClick={() => setOpen("")}>
                                Cancel
                            </Button>
                        </Stack>
                    </DialogContent>
                </Dialog>

            </Container>
        </Box>
    );
}
